import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { createShareableDevUrl } from '../../../utils/createShareableDevUrl';
import { clearStoredDevSettings } from '../../../utils/devSettings/clearStoredDevSettings';
import { devSettingsSchema } from '../../../utils/devSettings/devSettings.schema';
import type { DevSettings } from '../../../utils/devSettings/devSettings.types';
import { setStoredDevSettings } from '../../../utils/devSettings/setStoredDevSettings';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import { getDevSettings } from '../../../utils/getDevSettings';
import { PREBAKED_ENVIRONMENTS } from '../../constants/prebakedEnvironments';
import { PhantomLogsSection } from './PhantomLogsSection';

const formSchema = devSettingsSchema;

const QUICK_DOMAINS = [
  { label: 'Prod', url: 'https://app.dynamicauth.com/' },
  { label: 'Preprod', url: 'https://app.dynamic-preprod.xyz/' },
  { label: 'Local', url: 'http://localhost:4200/' },
] as const;

export const DevSettingsDialog: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: getDevSettings(),
    resolver: zodResolver(formSchema),
  });

  const values = form.watch();

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setStoredDevSettings({ settings: values });
    window.location.reload();
  };

  const handleCopyUrl = async () => {
    const shareableUrl = createShareableDevUrl({
      baseUrl: window.location.href,
      devSettings: form.getValues(),
    });

    await navigator.clipboard.writeText(shareableUrl);
    toast.success('URL copied to clipboard!');
  };

  const handleClearSettings = () => {
    clearStoredDevSettings();
    window.location.reload();
  };

  const getExtensionKeys = (): (keyof DevSettings['extensions'])[] => {
    return Object.keys(
      form.getValues('extensions')
    ) as (keyof DevSettings['extensions'])[];
  };

  const handleEnableAll = () => {
    getExtensionKeys().forEach((extensionKey) => {
      form.setValue(`extensions.${extensionKey}`, true);
    });
  };

  const handleDisableAll = () => {
    getExtensionKeys().forEach((extensionKey) => {
      form.setValue(`extensions.${extensionKey}`, false);
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.key === 'e') {
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const ExtensionToggleField: FC<{
    extensionName: keyof DevSettings['extensions'];
  }> = ({ extensionName }) => {
    const mapExtensionNameToDisplayName: Record<
      keyof DevSettings['extensions'],
      string
    > = {
      aptos: 'Aptos',
      bitcoin: 'Bitcoin',
      evm: 'EVM',
      phantomRedirectSolana: 'Phantom Redirect (Solana)',
      solana: 'Solana',
      starknet: 'Starknet',
      sui: 'SUI',
      tron: 'Tron',
      walletConnectEvm: 'WalletConnect EVM',
      walletConnectSolana: 'WalletConnect Solana',
      zerodev: 'Zerodev',
    };

    return (
      <FormField
        key={extensionName}
        control={form.control}
        name={`extensions.${extensionName}`}
        render={({ field }) => (
          <FormItem className="flex items-center space-x-2 space-y-0">
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel className="text-sm cursor-pointer">
              {mapExtensionNameToDisplayName[extensionName]}
            </FormLabel>
          </FormItem>
        )}
      />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card/80 backdrop-blur-sm border border-border/50 shadow-[0_1px_6px_rgba(0,0,0,0.06)] hover:bg-card hover:border-border hover:shadow-[0_2px_10px_rgba(0,0,0,0.08)] transition-all cursor-pointer"
        >
          <kbd className="text-[10px] font-medium text-muted-foreground bg-muted rounded px-1.5 py-0.5 font-mono leading-none">
            ⌘E
          </kbd>
          <span className="text-[11px] font-medium text-muted-foreground">
            Dev Options
          </span>
        </button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dev Options</DialogTitle>
        </DialogHeader>

        <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="phantom-logs">👻 Phantom Logs</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-4">
                  <FormItem>
                    <FormLabel>Prebaked</FormLabel>
                    <FormControl>
                      <Select
                        value={[values.apiBaseUrlOrigin, values.apiBaseUrlPath, values.environmentId].join('<>')}
                        onValueChange={(value) => {
                          if (!value) return;

                          const [apiBaseUrlOrigin, apiBaseUrlPath, environmentId] = value.split('<>');

                          form.setValue('apiBaseUrlOrigin', apiBaseUrlOrigin ?? '');
                          form.setValue('apiBaseUrlPath', apiBaseUrlPath ?? '');
                          form.setValue('environmentId', environmentId ?? '');
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a prebaked environment" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {PREBAKED_ENVIRONMENTS.map(({ devSettings, name }) => {
                              const value = [
                                devSettings.apiBaseUrlOrigin,
                                devSettings.apiBaseUrlPath,
                                devSettings.environmentId,
                              ].join('<>');

                              return (
                                <SelectItem key={value} value={value}>
                                  {name}
                                  {' - '}
                                  {devSettings.apiBaseUrlOrigin.includes('preprod')
                                    ? 'preprod'
                                    : 'prod'}
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>

                  <hr className="my-6" />

                  <FormField
                    control={form.control}
                    name="environmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Environment ID</FormLabel>
                        <FormControl>
                          <Input placeholder="<env id>" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <FormLabel>API Base URL</FormLabel>
                    <div className="flex gap-2">
                      <FormField
                        control={form.control}
                        name="apiBaseUrlOrigin"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input placeholder="https://app.dynamicauth.com/" {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="apiBaseUrlPath"
                        render={({ field }) => (
                          <FormItem className="w-24">
                            <FormControl>
                              <Input placeholder="api/v0/" {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex gap-1.5">
                      {QUICK_DOMAINS.map(({ label, url }) => (
                        <button
                          key={url}
                          type="button"
                          onClick={() => form.setValue('apiBaseUrlOrigin', url)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-md border transition-colors ${
                            values.apiBaseUrlOrigin === url
                              ? 'bg-primary/10 border-primary/30 text-primary'
                              : 'bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted hover:border-border'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <FormLabel>Extensions</FormLabel>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={handleEnableAll}
                        >
                          Enable All
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={handleDisableAll}
                        >
                          Disable All
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {Object.keys(form.getValues('extensions')).map((extension) => (
                        <ExtensionToggleField
                          key={extension}
                          extensionName={extension as keyof DevSettings['extensions']}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleClearSettings}
                    >
                      Clear
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCopyUrl}>
                      Copy URL
                    </Button>
                  </div>
                  <Button type="submit">Select</Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="phantom-logs">
            <PhantomLogsSection />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
